import React, { useState, useEffect, useCallback } from 'react';
import {
  HStack,
  Input,
  IconButton,
  Flex,
  VStack,
  Heading,
  Stack,
  Skeleton,
  Text,
  Link,
  createListCollection,
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { FaSearch } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { Table } from 'antd';
import { RiPencilFill } from 'react-icons/ri';
import { FaUserSlash, FaUserShield } from 'react-icons/fa';
import { debounce } from 'lodash';
import { PaginationBar } from '../../core/PaginationComponents';
import NoData from '../_components/NoData';
import ContactBedge from './ContactBedge';
import RightAngleIcon from '../../core/RightAngleIcon';
import { Tooltip } from '@/components/ui/tooltip';
import { getStatusColor } from '../../../helpers';
import AddContact from './AddContact';
import { actions, fetchContacts } from '../../../reducers/reducer_food_safety_quality';
import Filters from '../_components/Filters';
import api from '../../../api';
import DeleteContact from './DeleteContact';
import { getTenantFromDomain } from '../../../helpers/tenant';
import { InputGroup } from '../../ui/input-group';
import { toaster } from '@/components/ui/toaster';
import { SelectRoot, SelectTrigger, SelectValueText, SelectContent, SelectItem } from '@/components/ui/select';
import { getFontFamilyForTenant } from '../../../utils';

const paginationOptions = createListCollection({
  items: [
    { value: 10, label: 'Show 10' },
    { value: 20, label: 'Show 20' },
    { value: 30, label: 'Show 30' },
    { value: 40, label: 'Show 40' },
    { value: 50, label: 'Show 50' },
  ],
});

const tenant = getTenantFromDomain();
const fontFamilyStyle = { fontFamily: getFontFamilyForTenant() };

const columns = [
  {
    title: <span style={fontFamilyStyle}>NAME</span>,
    dataIndex: 'name',
    render: name => <ContactBedge name={name} />,
  },
  {
    title: <span style={fontFamilyStyle}>CONTACT OF</span>,
    dataIndex: 'contact_of',
    render: (name, record) => `${record.associated_to ?? ''} ${record.associated_to ? `(${name})` : name}`,
  },
  {
    title: <span style={fontFamilyStyle}>ROLE</span>,
    dataIndex: 'role',
    sorter: (a, b) => {
      const resourceA = a.role.toUpperCase();
      const resourceB = b.role.toUpperCase();
      if (resourceA < resourceB) {
        return -1;
      }
      if (resourceA > resourceB) {
        return 1;
      }

      return 0;
    },
  },
  {
    title: <span style={fontFamilyStyle}>CONTACT TYPE</span>,
    dataIndex: 'contact_type',
    sorter: (a, b) => {
      const resourceA = a.contact_type.toUpperCase();
      const resourceB = b.contact_type.toUpperCase();
      if (resourceA < resourceB) {
        return -1;
      }
      if (resourceA > resourceB) {
        return 1;
      }

      return 0;
    },
  },
  {
    title: <span style={fontFamilyStyle}>CONTACT NUMBER</span>,
    dataIndex: 'mobile',
    render: (mobile, record) => `${record.country_code} ${mobile}`,
    sorter: (a, b) => {
      const resourceA = a.mobile.toUpperCase();
      const resourceB = b.mobile.toUpperCase();
      if (resourceA < resourceB) {
        return -1;
      }
      if (resourceA > resourceB) {
        return 1;
      }

      return 0;
    },
  },
  {
    title: <span style={fontFamilyStyle}>EMAIL ADDRESS</span>,
    dataIndex: 'email',
    render: email => (
      <Link
        _hover={{ color: 'inherit', textDecoration: 'underline' }}
        _active={{ color: 'inherit', textDecoration: 'underline' }}
        textDecoration="underline"
        href={`mailto:${email}`}
      >
        {email}
      </Link>
    ),
    sorter: (a, b) => {
      const resourceA = a.email.toUpperCase();
      const resourceB = b.email.toUpperCase();
      if (resourceA < resourceB) {
        return -1;
      }
      if (resourceA > resourceB) {
        return 1;
      }

      return 0;
    },
  },
  {
    title: <span style={fontFamilyStyle}>STATUS</span>,
    dataIndex: 'status',
    render: status => (
      <Text
        key={status}
        as="span"
        fontSize="11px"
        fontWeight="500"
        letterSpacing="0.4px"
        padding="4px 12px"
        borderRadius="6px"
        background={getStatusColor(status).bgColor}
        color={getStatusColor(status).color}
        textTransform="capitalize"
      >
        {status}
      </Text>
    ),
  },
  {
    title: <span style={fontFamilyStyle}>ACTIONS</span>,
    dataIndex: 'actions',
  },
];

const domain = tenant.includes('cfc') ? 'CFCO' : 'FMG';
const filtersList = [
  { label: 'All', value: {} },
  { label: 'Alphabetically (A-Z)', value: { sortby: 'alphabetic' } },
  { label: 'Recently Added', value: { sortby: 'recent' } },
  { label: 'Active', value: { status: 'Active' } },
  { label: 'Deactivated', value: { status: 'Deactivated' } },
  { label: `${domain} Users`, value: { contact_of: `${domain}` } },
  { label: 'Grinders', value: { contact_of: 'Grinder' } },
  { label: 'Sponsor', value: { contact_of: 'Sponsor' } },
];

/**
 * Renders the ImportantContacts component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isOpenAddPopup - Flag indicating whether the edit popup is open.
 * @param {function} props.setIsOpenAddPopup - Function to set the state of isOpenAddPopup.
 * @returns {JSX.Element} The ImportantContacts component.
 */
const ImportantContacts = ({ isOpenAddPopup, setIsOpenAddPopup }) => {
  const dispatch = useDispatch();
  const { deleteContact } = actions;
  const { contacts, loading } = useSelector(state => ({
    contacts: state.food_safety_quality.contacts,
    loading: state.food_safety_quality.loading,
  }));
  const [editItem, setEditItem] = useState(null);
  const [filters, setFilters] = useState({});
  // const toast = usetoaster.create();

  const [paginationState, setPaginationState] = useState({
    itemsPerPage: 10,
    currentPage: 1,
    query: '',
  });

  const onEditHandler = _item => {
    setEditItem(_item);
    setIsOpenAddPopup(true);
  };

  const handleItemsPerPageSelect = e => {
    const currentPageValue = 1;
    const itemsPerPageValue = parseInt(e.value[0], 10);

    setPaginationState({
      ...paginationState,
      itemsPerPage: itemsPerPageValue,
      currentPage: currentPageValue,
    });
  };

  const handlePageClick = selected => {
    setPaginationState({ ...paginationState, currentPage: selected });
  };

  const deleteContactHandler = async id => {
    try {
      const response = await api.delete(`/op-importantcontact/${id}`);
      if (response.status === 204) {
        dispatch(deleteContact(id));
        // toaster.create({
        //   title: 'Contact deleted successfully',
        //   position: 'top-right',
        //   status: 'success',
        //   duration: 3000,
        //   isClosable: true,
        // });
      } else {
        throw new Error(`Failed to delete contact. ${response.error}`);
      }
    } catch (error) {
      // toaster.create({
      //   title: error.message,
      //   position: 'top-right',
      //   status: 'error',
      //   duration: 3000,
      //   isClosable: true,
      // });
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      const response = await api.patch(`/deactivate-importantcontact/${id}`, { status });
      if (response.ok) {
        const json = await response.json();
        dispatch(actions.updateContact(json));
        toaster.create({
          title: 'Contact status updated successfully',
          position: 'top-right',
          type: 'success',
          duration: 3000,
          closable: true,
        });
      } else {
        throw new Error(`Failed to update contact status. ${response.error}`);
      }
    } catch (error) {
      toaster.create({
        title: error.message,
        position: 'top-right',
        type: 'error',
        duration: 3000,
        closable: true,
      });
    }
  };

  const handleSearch = e => {
    const { value: searchInputValue } = e.target;

    setPaginationState(prevState => ({
      ...prevState,
      currentPage: 1,
      query: searchInputValue,
    }));
  };

  const fetchContactsHandler = useCallback(
    debounce(pState => dispatch(fetchContacts(pState)), 300),
    []
  );

  useEffect(() => {
    fetchContactsHandler({ ...paginationState, ...filters });
  }, [paginationState.currentPage, paginationState.itemsPerPage, paginationState.query, filters]);

  useEffect(() => {
    if (!isOpenAddPopup) {
      setEditItem(null);
    }
  }, [isOpenAddPopup]);

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" width="100%" marginBlock="20px">
        <HStack spacing="10px">
          <RightAngleIcon />
          <Heading className="dashboard-section__heading" fontSize="15px">
            Important Contacts
          </Heading>
        </HStack>

        <Flex alignItems="center">
          <Filters
            value={filters}
            onChange={value => {
              setFilters(value);
              setPaginationState(prevState => ({
                ...prevState,
                currentPage: 1,
              }));
            }}
            options={filtersList}
          />
          <InputGroup marginTop="5px !important" width="264px" startElement={<FaSearch color="#0000005E" />}>
            <Input
              marginLeft="auto !important"
              placeholder="Search"
              width="100%"
              height="40px"
              fontWeight="normal !important"
              value={paginationState.query}
              backgroundColor="#ffffff"
              onChange={handleSearch}
            />
          </InputGroup>
        </Flex>
      </Flex>
      <VStack
        align="stretch"
        spacing="20px"
        padding="30px"
        boxShadow="md"
        borderRadius="6px"
        backgroundColor="card.default"
        width="100%"
        minHeight="calc(100vh - 320px)"
      >
        {loading && (
          <Stack>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
              <Skeleton key={i} height="25px" />
            ))}
          </Stack>
        )}

        {!loading && contacts?.items?.length ? (
          <>
            <Table
              pagination={{
                pageSize: paginationState.itemsPerPage,
                current: paginationState.currentPage,
                style: { display: 'none' },
              }}
              rowClassName="food-safety-table-row"
              className="food-safety-table"
              dataSource={contacts?.items?.map(itm => ({
                ...itm,
                actions: (
                  <Flex gap={3} css={{ gap: '10px' }}>
                    <Tooltip content="Edit">
                      <IconButton
                        width="24px"
                        height="24px"
                        padding="0"
                        minW="auto"
                        border="1px solid #D9DADF"
                        borderRadius="2px"
                        color="#00000099"
                        backgroundColor="#fff"
                        onClick={() => onEditHandler(itm)}
                      >
                        <RiPencilFill size={12} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip content={itm.status === 'Active' ? 'Deactivate' : 'Active'}>
                      <IconButton
                        width="24px"
                        height="24px"
                        padding="0"
                        minW="auto"
                        border="1px solid #D9DADF"
                        borderRadius="2px"
                        color="#00000099"
                        backgroundColor="#fff"
                        onClick={() => updateContactStatus(itm.id, itm.status === 'Active' ? 'Deactivated' : 'Active')}
                      >
                        {itm.status === 'Active' ? <FaUserSlash size={12} /> : <FaUserShield size={12} />}
                      </IconButton>
                    </Tooltip>
                    <DeleteContact onDelete={() => deleteContactHandler(itm.id)} />
                  </Flex>
                ),
              }))}
              columns={columns}
            />
            <Flex marginTop="auto !important">
              <HStack marginLeft="auto" minW="100px">
                <SelectRoot
                  collection={paginationOptions}
                  width="120px"
                  value={[paginationState.itemsPerPage]}
                  onValueChange={handleItemsPerPageSelect}
                >
                  <SelectTrigger>
                    <SelectValueText placeholder="Show 20" />
                  </SelectTrigger>
                  <SelectContent>
                    {paginationOptions.items.map(opt => (
                      <SelectItem item={opt} key={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              </HStack>
              <PaginationBar
                currentPage={paginationState.currentPage}
                pages={contacts?.pages}
                onPageChange={page => handlePageClick(page)}
                justifyContent="flex-end"
              />
            </Flex>
          </>
        ) : (
          !loading && <NoData />
        )}
        {isOpenAddPopup && (
          <AddContact editItem={editItem} isOpenAddPopup={isOpenAddPopup} setIsOpenAddPopup={setIsOpenAddPopup} />
        )}
      </VStack>
    </>
  );
};

ImportantContacts.propTypes = {
  isOpenAddPopup: PropTypes.bool,
  setIsOpenAddPopup: PropTypes.func,
};

export default ImportantContacts;
